import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useLoaderData } from "react-router-dom";
import { toast } from "react-toastify";

import DefaultApiService from "../../../../infrastructure/api/default";
import useCheckPermission from "../../../common/hooks/useCheckPermission";
import useGetPageInfo from "../../../common/hooks/useGetPageInfo";
import BreadcrumbsComponent from "../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../components/shared/dropdown";
import JalaliDatePicker from "../../../components/shared/jalali-datepicker";
import ReportDataShow from "../../../components/shared/report/ReportDataShow";
import Selector from "../../../components/shared/selector";
import { toMiladyFullDate } from "../../../middleware/middleware";

const todayRange = () => {
  const today = toMiladyFullDate(new Date());
  return { start: today, end: today };
};

const normalizeEnumList = (list) =>
  (Array.isArray(list) ? list : []).map((item) => ({
    name: item.name ?? item.Name ?? item.title ?? item.Title ?? "",
    value: item.value ?? item.Value ?? "",
  }));

const normalizeUsers = (users) =>
  (Array.isArray(users) ? users : []).map((user) => ({
    ...user,
    id: user.id ?? user.Id,
    fullName: user.fullName ?? user.FullName ?? "",
    isChecked: false,
  }));

const UserLoginReportPage = () => {
  const pageInfo = useGetPageInfo();
  const loaderData = useLoaderData("root") || {};

  const resultKindOptions = useMemo(
    () => normalizeEnumList(loaderData.resultKind ?? loaderData.ResultKind),
    [loaderData]
  );
  const periodKindOptions = useMemo(
    () => normalizeEnumList(loaderData.periodKind ?? loaderData.PeriodKind),
    [loaderData]
  );
  const initialUsers = useMemo(
    () => normalizeUsers(loaderData.users ?? loaderData.Users),
    [loaderData]
  );

  const [loading, setLoading] = useState(false);
  const [selectorKey, setSelectorKey] = useState(0);
  const [showFilterContainer, setShowFilterContainer] = useState(true);
  const [filterValues, setFilterValues] = useState(() => {
    const { start, end } = todayRange();
    return {
      Users: [],
      ResultKind: "All",
      PeriodKind: "Detail",
      StartDate: start,
      EndDate: end,
    };
  });
  const [tableData, setTableData] = useState({
    columns: [],
    data: [],
    title: "",
  });
  const [chartData, setChartData] = useState({
    data: [],
  });

  const onChangeUser = (checked) => {
    setFilterValues((old) => ({
      ...old,
      Users: (checked || []).map((id) => String(id)),
    }));
  };

  const onClearFilterValues = () => {
    const { start, end } = todayRange();
    setFilterValues({
      Users: [],
      ResultKind: "All",
      PeriodKind: "Detail",
      StartDate: start,
      EndDate: end,
    });
    setSelectorKey((key) => key + 1);
    setTableData({ columns: [], data: [], title: "" });
    setChartData({ data: [] });
  };

  const onGetReportData = async () => {
    try {
      setLoading(true);

      const res = await DefaultApiService.postApi(
        "UM/Yeksa/UserLoginReport/GetData",
        filterValues
      );

      if (res?.isSuccess === false) {
        toast.error(res?.message || "بارگذاری گزارش ناموفق بود.");
        return;
      }

      const payload = res?.data ?? res?.Data ?? {};
      const table = payload?.tableData ?? payload?.TableData;
      const chart = payload?.chartData ?? payload?.ChartData;

      if (table?.columns || table?.Columns) {
        const columns = table.columns ?? table.Columns ?? [];
        const rawRows = table.data ?? table.Data ?? [];

        setTableData({
          title: table.title ?? table.Title ?? "گزارش ورود کاربران",
          columns: columns.map((x) => ({
            id: x,
            label: x,
          })),
          data: rawRows.map((row) => {
            const item = {};
            for (let i = 0; i < columns.length; i++) {
              item[columns[i]] = row[i];
            }
            return item;
          }),
        });
      } else {
        setTableData({ columns: [], data: [], title: "" });
      }

      if (chart?.xValues || chart?.XValues) {
        const xValues = chart.xValues ?? chart.XValues ?? [];
        const seriesTitle = chart.seriesTitle ?? chart.SeriesTitle ?? [];
        const seriesData = chart.data ?? chart.Data ?? [];

        const finalChartData = xValues.map((item, index) => ({
          x: item,
          index: index + 1,
        }));

        for (let i = 0; i < seriesData.length; i++) {
          const yData = seriesData[i];
          const seriesName = seriesTitle[i];
          for (let j = 0; j < yData.length; j++) {
            finalChartData[j][seriesName] = yData[j];
          }
        }

        setChartData({
          ...chart,
          data: finalChartData,
        });
      } else {
        setChartData({ data: [] });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={pageInfo.title}
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<SearchIcon />}
                  onClick={onGetReportData}
                  disabled={loading}
                >
                  جستجو
                  {loading ? (
                    <CircularProgress
                      color="warning"
                      size={16}
                      sx={{ ml: 2 }}
                    />
                  ) : null}
                </Button>
                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ mx: 1 }}
                />
                <Button
                  variant="text"
                  color="gray"
                  startIcon={<HighlightOffIcon />}
                  onClick={onClearFilterValues}
                >
                  حذف فیلترها
                </Button>
                <Divider
                  orientation="vertical"
                  variant="middle"
                  flexItem
                  sx={{ mx: 1 }}
                />
                <Button
                  variant="text"
                  color="primary"
                  startIcon={<CandlestickChartIcon />}
                  onClick={() => setShowFilterContainer(!showFilterContainer)}
                >
                  {showFilterContainer ? "پنهان کردن فیلتر" : "نشان دادن فیلتر"}
                </Button>
              </Box>
            }
          />
          <CardContent>
            <Grid container>
              <Grid container item xs={12} md={12} className="filter-bar">
                <Grid item container xs={12} md={12} spacing={2}>
                  <Grid item xs={12} md={4} lg={2}>
                    <DropdownPicker
                      label="به تفکیک"
                      value={filterValues.ResultKind}
                      onChange={(event) => {
                        setFilterValues((old) => ({
                          ...old,
                          ResultKind: event.target.value,
                        }));
                      }}
                      list={resultKindOptions}
                      optionName="name"
                      optionValue="value"
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} md={4} lg={2}>
                    <DropdownPicker
                      label="بازه زمانی"
                      value={filterValues.PeriodKind}
                      onChange={(event) => {
                        setFilterValues((old) => ({
                          ...old,
                          PeriodKind: event.target.value,
                        }));
                      }}
                      list={periodKindOptions}
                      optionName="name"
                      optionValue="value"
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} md={4} lg={2}>
                    <JalaliDatePicker
                      label="از تاریخ"
                      fullWidth
                      value={filterValues.StartDate}
                      onChange={(value) => {
                        setFilterValues((old) => ({
                          ...old,
                          StartDate: value,
                        }));
                      }}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} md={4} lg={2}>
                    <JalaliDatePicker
                      label="تا تاریخ"
                      fullWidth
                      value={filterValues.EndDate}
                      onChange={(value) => {
                        setFilterValues((old) => ({
                          ...old,
                          EndDate: value,
                        }));
                      }}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Collapse
                in={showFilterContainer}
                timeout="auto"
                unmountOnExit
                sx={{ width: "100%" }}
              >
                <Grid container>
                  <Grid item xs={12} md={6} lg={2}>
                    <Selector
                      key={selectorKey}
                      title="همه کاربران"
                      options={initialUsers}
                      optionName="fullName"
                      optionValue="id"
                      selectedValues={filterValues.Users}
                      onChange={onChangeUser}
                    />
                  </Grid>
                </Grid>
              </Collapse>

              <ReportDataShow
                tableData={tableData}
                chartData={chartData}
                loading={loading}
                hasExportPdf={useCheckPermission("pdf")}
                hasExportExcel={useCheckPermission("excel")}
                exportApi={pageInfo.link}
                filterValues={filterValues}
              />
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default UserLoginReportPage;
